import React from 'react';

import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';
import {
    getCurrentFeatures,
    getSearchQueryUid,
    getSearchQuery,
    getTotalResultsCount,
    getSelectedFilter,
    getSearchEngine,
    getSearchTraceId,
} from '@lumapps/search/ducks/selectors';
import { BaseSearchResult } from '@lumapps/search/types';
import { useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';

import { GenericResultItem } from '../types';
import { getEntityProps } from '../utils/getEntityProps';

export const useResultsGenericItemsConverter = (
    items?: BaseSearchResult[],
    defaultValue: GenericResultItem[] | null = [],
): GenericResultItem[] | null => {
    const { translate, translateKey } = useTranslate();
    const features = useSelector(getCurrentFeatures);
    const currentInstanceId = useSelector(instanceIdSelector);
    const currentLanguage = useSelector(currentLanguageSelector);
    const searchQueryUid = useSelector(getSearchQueryUid);
    const searchQuery = useSelector(getSearchQuery);
    const totalCount = useSelector(getTotalResultsCount);
    const selectedTab = useSelector(getSelectedFilter);
    const searchEngine = useSelector(getSearchEngine);
    const traceId = useSelector(getSearchTraceId);

    return React.useMemo(() => {
        if (!items) {
            return defaultValue;
        }

        return items.map((item) =>
            getEntityProps({
                item,
                translate,
                translateKey,
                features,
                currentInstanceId,
                searchQuery,
                searchQueryUid,
                currentLanguage,
                shouldAddBaseSlug: true,
                totalCount: totalCount || undefined,
                selectedTab: selectedTab || undefined,
                searchEngine,
                traceId,
            }),
        );
    }, [
        items,
        defaultValue,
        translate,
        translateKey,
        features,
        currentInstanceId,
        searchQuery,
        searchQueryUid,
        currentLanguage,
        totalCount,
        selectedTab,
        searchEngine,
        traceId,
    ]);
};

export const useResultsGenericItemConverter = (item: BaseSearchResult): GenericResultItem => {
    const { translate, translateKey } = useTranslate();
    const features = useSelector(getCurrentFeatures);
    const currentInstanceId = useSelector(instanceIdSelector);
    const currentLanguage = useSelector(currentLanguageSelector);
    const searchQueryUid = useSelector(getSearchQueryUid);
    const searchQuery = useSelector(getSearchQuery);
    const searchEngine = useSelector(getSearchEngine);
    const traceId = useSelector(getSearchTraceId);

    return React.useMemo(
        () =>
            getEntityProps({
                item,
                translate,
                translateKey,
                features,
                currentInstanceId,
                searchQuery,
                searchQueryUid,
                currentLanguage,
                shouldAddBaseSlug: true,
                searchEngine,
                traceId,
            }),
        [
            item,
            translate,
            translateKey,
            features,
            currentInstanceId,
            searchQuery,
            searchQueryUid,
            currentLanguage,
            searchEngine,
            traceId,
        ],
    );
};
